import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Radio, CalendarClock, UserPlus, UserCheck, Loader2, Music4, Clock, ListMusic, Download } from "lucide-react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { HOST, HOST_SCHEDULE, nextLive, formatNextLive } from "@/lib/host";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const fmtHour = (h, m) => { const d = new Date(); d.setHours(h, m, 0, 0); return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); };

export default function HostBio() {
  const { user } = useAuth();
  const [episodes, setEpisodes] = useState([]);
  const [followers, setFollowers] = useState(0);
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const next = nextLive();

  useEffect(() => {
    Promise.all([
      api.get("/episodes").catch(() => ({ data: [] })),
      api.get(`/hosts/${HOST.id}`).catch(() => ({ data: {} })),
    ]).then(([eps, h]) => {
      setEpisodes(eps.data || []);
      setFollowers(h.data?.followers || 0);
      setFollowing(!!h.data?.is_following);
      setLoading(false);
    });
  }, []);

  const toggleFollow = async () => {
    if (!user) { toast.error("Please sign in to follow David"); return; }
    setFollowing((f) => !f);
    setFollowers((n) => n + (following ? -1 : 1));
    try {
      const { data } = await api.post(`/hosts/${HOST.id}/follow`);
      setFollowing(data.following);
    } catch (e) { toast.error("Could not update"); }
  };

  return (
    <div className="pb-32">
      <div className="px-4 sm:px-6 lg:px-12 pt-6">
        <Link to="/live" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-[#E6B34A]"><ArrowLeft className="w-4 h-4" /> Back to the show</Link>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden mx-6 lg:mx-12 mt-4 rounded-3xl border border-[#E6B34A]/20 bg-gradient-to-br from-[#161F47] via-[#141C42] to-[#0C1338]" data-testid="hostbio-hero">
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-[#E6B34A]/10 blur-3xl" />
        <div className="relative grid md:grid-cols-2 gap-0 items-end">
          <div className="flex justify-center md:justify-start items-end pt-8 md:pt-0 px-6 md:pl-10">
            <img src={HOST.photo} alt={HOST.name} data-testid="hostbio-photo" className="w-auto h-[320px] md:h-[420px] object-contain object-bottom drop-shadow-[0_20px_40px_rgba(0,0,0,0.5)]" />
          </div>
          <div className="p-8 md:p-12">
            <span className="overline text-[#E6B34A]">Your Host</span>
            <h1 className="font-display font-black text-4xl lg:text-6xl tracking-tighter leading-none mt-2">{HOST.name}</h1>
            <p className="text-[#E6B34A] font-semibold mt-3">{HOST.role}</p>
            <div className="flex items-center gap-4 mt-5">
              <button onClick={toggleFollow} data-testid="hostbio-follow" className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-colors ${following ? "bg-white/10 text-white hover:bg-white/15" : "bg-[#E6B34A] text-[#0A1030] hover:bg-[#CF9A34]"}`}>
                {following ? <><UserCheck className="w-4 h-4" /> Following</> : <><UserPlus className="w-4 h-4" /> Follow David</>}
              </button>
              <span className="text-sm text-zinc-400" data-testid="hostbio-followers"><b className="text-white">{followers}</b> follower{followers === 1 ? "" : "s"}</span>
            </div>
          </div>
        </div>
      </section>

      <div className="px-4 sm:px-6 lg:px-12 grid lg:grid-cols-3 gap-8 mt-10">
        {/* Bio + Episodes */}
        <div className="lg:col-span-2">
          <h2 className="font-display font-extrabold text-xl tracking-tight mb-3">About David</h2>
          <p className="text-zinc-300 leading-relaxed" data-testid="hostbio-about">{HOST.bio}</p>
          <div className="flex flex-wrap gap-2 mt-5">
            {HOST.tags.map((t) => <span key={t} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-[#E6B34A]/10 text-[#E6B34A]">{t}</span>)}
          </div>

          <h2 className="font-display font-extrabold text-xl tracking-tight mt-10 mb-5 flex items-center gap-2"><Radio className="w-5 h-5 text-[#E6B34A]" /> Past Episodes</h2>
          {loading ? <div className="flex py-10"><Loader2 className="w-6 h-6 animate-spin text-[#E6B34A]" /></div> :
            episodes.length === 0 ? <p className="text-zinc-500">No episodes published yet — follow to be notified.</p> :
            (() => {
              const groups = {};
              episodes.forEach((ep) => { const k = ep.series || "Standalone Episodes"; (groups[k] = groups[k] || []).push(ep); });
              const order = Object.keys(groups).sort((a, b) => (a === "Standalone Episodes" ? 1 : 0) - (b === "Standalone Episodes" ? 1 : 0));
              return (
                <div className="space-y-8" data-testid="hostbio-episodes">
                  {order.map((series) => (
                    <div key={series} data-testid={`series-${series.replace(/\s+/g, "-").toLowerCase()}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <ListMusic className="w-4 h-4 text-[#E6B34A]" />
                        <h3 className="font-display font-bold tracking-tight">{series}</h3>
                        <span className="text-xs text-zinc-500">{groups[series].length} episode{groups[series].length === 1 ? "" : "s"}</span>
                      </div>
                      <div className="space-y-4">
                        {groups[series].map((ep) => (
                          <div key={ep.id} className="rounded-2xl border border-white/5 bg-[#141C42] p-5 flex gap-4" data-testid={`hostbio-ep-${ep.id}`}>
                            <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#0C1338] border border-white/10 flex items-center justify-center shrink-0">
                              {ep.has_cover ? <img src={`${API}${ep.cover_url}`} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} /> : null}
                              <div className="w-full h-full items-center justify-center" style={{ display: ep.has_cover ? "none" : "flex" }}><Music4 className="w-5 h-5 text-zinc-600" /></div>
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="font-display font-bold text-lg tracking-tight truncate">{ep.title}</h3>
                              {ep.description && <p className="text-sm text-zinc-400 mt-0.5 line-clamp-2">{ep.description}</p>}
                              <audio controls className="w-full mt-2 h-9"><source src={`${API}/episodes/${ep.id}/audio`} /></audio>
                              <a href={`${API}/episodes/${ep.id}/audio?download=1`} download data-testid={`download-ep-${ep.id}`} className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-[#E6B34A] hover:underline">
                                <Download className="w-3.5 h-3.5" /> Download episode
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
        </div>

        {/* Schedule */}
        <div>
          <div className="rounded-2xl border border-[#E6B34A]/20 bg-[#141C42] p-6 sticky top-6" data-testid="hostbio-schedule">
            <h2 className="font-display font-extrabold text-lg tracking-tight flex items-center gap-2 mb-1"><CalendarClock className="w-5 h-5 text-[#E6B34A]" /> Show Schedule</h2>
            {next && (
              <div className="rounded-xl bg-[#E6B34A]/10 border border-[#E6B34A]/20 p-3 mt-4 mb-5" data-testid="hostbio-next">
                <p className="overline text-[#E6B34A] flex items-center gap-1.5"><Clock className="w-3 h-3" /> Next live</p>
                <p className="font-semibold mt-1">{next.title}</p>
                <p className="text-sm text-zinc-300">{formatNextLive(next)}</p>
              </div>
            )}
            <div className="space-y-2">
              {HOST_SCHEDULE.map((s, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg bg-[#0C1338] px-3 py-2.5">
                  <div>
                    <p className="text-sm font-semibold">{s.title}</p>
                    <p className="text-xs text-zinc-500">{DAYS[s.day]}</p>
                  </div>
                  <span className="text-sm text-[#E6B34A] font-semibold tabular-nums">{fmtHour(s.hour, s.minute)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
